import { createApiCall } from './index'

/** VRG-538 — an ROI-input link on a claim, with which way it bears on the default. */
export interface IAskClaimRoiMetric {
  id: string
  metric: string
  /** 'supports' | 'contradicts' | 'refines' */
  direction: string
  state: 'suggested' | 'confirmed'
}

export interface IAskClaim {
  id: string
  /** EXACT text from the article — verified as a substring before the row existed. */
  verbatimQuote: string
  value: number | null
  unit: string | null
  statDate: string | null
  category: string | null
  region: string | null
  period: string | null
  /** The piece we read. */
  asReportedBy: string | null
  articleUrl: string | null
  /** The study IT cites, where named. Null is honest; a guess is the failure this prevents. */
  originalSource: string | null
  originalSourceUrl: string | null
  /** Surrounding paragraph, so a reviewer can judge without opening the article. */
  contextSnippet: string | null
  /** Our arithmetic, not the source's. `derivation` shows the computation. */
  isDerived: boolean
  derivation: string | null
  /** 'primary' | 'calibration' — a calibration source is never a peer of a primary one. */
  evidenceTier: string
  /** What changes the figure's MEANING: denominator, reference year, population. */
  scopeCaveat: string | null
  verificationNote: string | null
  sampleSize: number | null
  /** Marketing-site inputs citing this. NOT an approval signal. */
  siteUsage: string[] | null
  /**
   * VRG-538 — ROI calculator inputs this claim is evidence for, suggested and confirmed
   * alike. ⚠️ Anything that ACTS on these must narrow to `state === 'confirmed'`.
   */
  roiMetrics: IAskClaimRoiMetric[]
  approvedAt: string | null
  rejectedAt: string | null
  rejectionReason: string | null
  sourceItemId: string | null
  itemTitle: string | null
  /** The source ARTICLE's own publish date — distinct from `statDate` (what the
   * figure itself is about). See `AskClaimRow.publishedAt`'s own doc (API side). */
  publishedAt: string | null
  createdAt: string
}

export interface ExtractionRunResult {
  itemId: string
  proposed: number
  /** Discarded because the quote was not a literal substring of the body. */
  rejectedByVerification: number
  skipped?: 'no_body' | 'unparseable'
  inputTokens?: number
  outputTokens?: number
}

export class AskClaimsApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'AskClaimsApiError'
  }
}

export const isMarketStatsForbidden = (error: unknown): boolean =>
  error instanceof AskClaimsApiError && error.status === 403

const ok = (response: any, fallback: string) => {
  if (response.status !== 200 && response.status !== 201) {
    throw new AskClaimsApiError(response?.data?.message || fallback, response?.status ?? 0)
  }
  return response.data
}

export const askClaimsApi = {
  async proposed(): Promise<IAskClaim[]> {
    const response = await createApiCall({ method: 'GET', url: '/internal/ask-claims/proposed' })
    return ok(response, 'Failed to load the approval queue')
  },

  async approved(): Promise<IAskClaim[]> {
    const response = await createApiCall({ method: 'GET', url: '/internal/ask-claims/approved' })
    return ok(response, 'Failed to load approved stats')
  },

  async rejected(): Promise<IAskClaim[]> {
    const response = await createApiCall({ method: 'GET', url: '/internal/ask-claims/rejected' })
    return ok(response, 'Failed to load rejected stats')
  },

  async extract(itemId: string): Promise<ExtractionRunResult> {
    const response = await createApiCall({
      method: 'POST',
      url: `/internal/ask-claims/extract/${itemId}`,
    })
    return ok(response, 'Could not extract from that item')
  },

  async approve(id: string): Promise<IAskClaim> {
    const response = await createApiCall({
      method: 'POST',
      url: `/internal/ask-claims/${id}/approve`,
    })
    return ok(response, "Couldn't approve that stat")
  },

  async reject(id: string, reason: string): Promise<IAskClaim> {
    const response = await createApiCall({
      method: 'POST',
      url: `/internal/ask-claims/${id}/reject`,
      data: { reason },
    })
    return ok(response, "Couldn't reject that stat")
  },

  /** ONE request for the whole selection — see `useBulkApproveClaims`'s doc for why. */
  async bulkApprove(ids: string[]): Promise<IAskClaim[]> {
    const response = await createApiCall({
      method: 'POST',
      url: '/internal/ask-claims/bulk-approve',
      data: { ids },
    })
    return ok(response, "Couldn't approve those stats")
  },

  async bulkReject(ids: string[], reason: string): Promise<IAskClaim[]> {
    const response = await createApiCall({
      method: 'POST',
      url: '/internal/ask-claims/bulk-reject',
      data: { ids, reason },
    })
    return ok(response, "Couldn't reject those stats")
  },
}
